import React, { useState, useEffect } from 'react';
import { db, storage } from '../../../../firebase-config';
import { doc, setDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Button, Card, Upload, Modal, message as antMessage, Input, Space, Popconfirm } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';

const ProofEditor = () => {
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [newProofTitle, setNewProofTitle] = useState('');

  useEffect(() => {
    fetchProofs();
  }, []);

  const fetchProofs = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'proofs'));
      const proofsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProofs(proofsData);
    } catch (error) {
      console.error('Error fetching proofs:', error);
      antMessage.error('Failed to load proofs.');
    }
    setLoading(false);
  };

  const handleUpload = async (file) => {
    if (!newProofTitle.trim()) {
      antMessage.warning('Please enter a title for the proof.');
      return false;
    }

    setUploading(true);
    try {
      // Upload image to Firebase Storage
      const storageRef = ref(storage, `proofs/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Save proof data to Firestore
      const proofData = {
        title: newProofTitle,
        imageUrl: downloadURL,
        alt: newProofTitle,
        createdAt: new Date().toISOString(),
        order: proofs.length + 1
      };

      const docRef = doc(collection(db, 'proofs'));
      await setDoc(docRef, proofData);

      antMessage.success('Proof added successfully!');
      setNewProofTitle('');
      fetchProofs(); // Refresh the list
    } catch (error) {
      console.error('Error uploading proof:', error);
      antMessage.error('Failed to upload proof.');
    }
    setUploading(false);
    return false; // Prevent default upload behavior
  };

  const handleDelete = async (proofId, imageUrl) => {
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'proofs', proofId));

      // Delete from Storage
      if (imageUrl) {
        const imageRef = ref(storage, imageUrl);
        try {
          await deleteObject(imageRef);
        } catch (storageError) {
          console.warn('Could not delete image from storage:', storageError);
        }
      }

      antMessage.success('Proof deleted successfully!');
      fetchProofs(); // Refresh the list
    } catch (error) {
      console.error('Error deleting proof:', error);
      antMessage.error('Failed to delete proof.');
    }
  };

  const handlePreview = (imageUrl) => {
    setPreviewImage(imageUrl);
    setPreviewVisible(true);
  };

  const handleCancelPreview = () => {
    setPreviewVisible(false);
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
        <p>Loading proofs...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
        Manage Proof Images
      </h1>
      
      {/* Add New Proof Section */}
      <Card title="Add New Proof" style={{ marginBottom: '24px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            placeholder="Enter proof title (e.g., 'Payment Proof 1')"
            value={newProofTitle}
            onChange={(e) => setNewProofTitle(e.target.value)}
            style={{ marginBottom: '16px' }}
          />
          <Upload
            beforeUpload={handleUpload}
            showUploadList={false}
            accept="image/*"
            disabled={uploading || !newProofTitle.trim()}
          >
            <Button 
              icon={<UploadOutlined />} 
              loading={uploading}
              disabled={!newProofTitle.trim()}
            >
              {uploading ? 'Uploading...' : 'Upload Proof Image'}
            </Button>
          </Upload>
        </Space>
      </Card>

      {/* Existing Proofs Section */}
      <Card title="Existing Proofs">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
          {proofs.map((proof) => (
            <Card
              key={proof.id}
              size="small"
              style={{ position: 'relative' }}
              bodyStyle={{ padding: '8px' }}
            >
              <div style={{ position: 'relative' }}>
                <img
                  src={proof.imageUrl}
                  alt={proof.alt}
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={() => handlePreview(proof.imageUrl)}
                />
                <div style={{ 
                  position: 'absolute', 
                  top: '8px', 
                  right: '8px',
                  background: 'rgba(0,0,0,0.7)',
                  borderRadius: '4px',
                  padding: '4px'
                }}>
                  <Popconfirm
                    title="Delete this proof?"
                    description="Are you sure you want to delete this proof image?"
                    onConfirm={() => handleDelete(proof.id, proof.imageUrl)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      style={{ color: 'white' }}
                      size="small"
                    />
                  </Popconfirm>
                </div>
              </div>
              <div style={{ 
                marginTop: '8px', 
                fontSize: '12px', 
                color: '#666',
                textAlign: 'center'
              }}>
                {proof.title}
              </div>
            </Card>
          ))}
        </div>
        
        {proofs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No proofs added yet. Add your first proof above.
          </div>
        )}
      </Card>

      {/* Image Preview Modal */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={handleCancelPreview}
        width="80%"
        style={{ top: 20 }}
      >
        <img
          alt="Preview"
          style={{ width: '100%', height: 'auto' }}
          src={previewImage}
        />
      </Modal>
    </div>
  );
};

export default ProofEditor; 